/**
 * RequestOptionsRenderer - Renderer for single request timeout settings
 *
 * Allows setting a timeout for individual HTTP requests (separate from action timeout).
 */

import { useState, useCallback } from 'react';
import { makeStyles, tokens, Text, Input, Field } from '@fluentui/react-components';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';
import type { RequestOptions } from '@/types/nodeSettings';
import { validateDuration } from '@/utils/validation';

import {
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  header: settingHeaderStyle,
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  input: {
    width: '150px',
  },
  hint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
});

export interface RequestOptionsRendererProps extends SettingRendererProps<RequestOptions> {
  label?: string;
  description?: string;
}

/**
 * RequestOptionsRenderer component for request timeout configuration
 */
export const RequestOptionsRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Request Timeout',
  description = 'Timeout for individual HTTP requests',
}: RequestOptionsRendererProps) => {
  const styles = useStyles();
  const [validationError, setValidationError] = useState<string | null>(null);

  const currentValue = setting.value ?? {};

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Validate duration
    const validation = validateDuration(value);
    setValidationError(validation.error ?? null);

    if (value) {
      onChange({ timeout: value });
    } else {
      onChange(undefined);
    }
  }, [onChange]);

  if (!setting.isSupported) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.label}>{label}</Text>
      </div>
      <Text className={styles.description}>{description}</Text>
      <div className={styles.inputContainer}>
        <Field
          label="Duration"
          {...(validationError ? { validationState: 'error' as const, validationMessage: validationError } : {})}
        >
          <Input
            className={styles.input}
            value={currentValue.timeout ?? ''}
            onChange={handleChange}
            placeholder="PT30S"
            disabled={readOnly}
            aria-label={label}
          />
        </Field>
        <Text className={styles.hint}>ISO 8601 duration (e.g., PT30S, PT2M)</Text>
      </div>
    </div>
  );
};

RequestOptionsRenderer.displayName = 'RequestOptionsRenderer';

registerSettingRenderer('requestOptions', RequestOptionsRenderer as React.ComponentType<SettingRendererProps<unknown>>);
